import React, { useState, useEffect } from 'react';
import QRCodeLib from 'qrcode';
import { useDomains } from '../../hooks/useDomains';
import { getLinkDomain, buildShortUrl } from '../../utils/domainUtils';

const ModernLinkCard = ({ link, onEdit, onDelete, onToggleSelect, isSelected, onShare }) => {
  const { domains } = useDomains();
  const [copySuccess, setCopySuccess] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [showQR, setShowQR] = useState(false);

  // Use dynamic domain resolution
  const domainName = getLinkDomain(link, domains);
  const shortUrl = buildShortUrl(link.shortCode, domainName);
  const domain = link.domainId ? domains.find(d => d.id === link.domainId) : null;

  const isExpired = link.expiresAt && new Date(link.expiresAt) <= new Date();
  const isPasswordProtected = !!link.password;

  // Generate QR code when needed
  useEffect(() => {
    if (showQR && !qrDataUrl && shortUrl) {
      generateQRCode();
    }
  }, [showQR, qrDataUrl, shortUrl]);

  const generateQRCode = async () => {
    try {
      const dataUrl = await QRCodeLib.toDataURL(shortUrl, {
        width: 120,
        margin: 1,
        color: {
          dark: '#1a1a1a',
          light: '#ffffff'
        }
      });
      setQrDataUrl(dataUrl);
    } catch (error) {
      console.error('❌ Error generating QR code:', error);
    }
  };

  const toggleQR = () => {
    setShowQR(!showQR);
  };

  const copyQRCode = async () => {
    if (!qrDataUrl) return;

    try {
      const response = await fetch(qrDataUrl);
      const blob = await response.blob();
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob })
      ]);
      setCopySuccess('QR Copied!');
      setTimeout(() => setCopySuccess(''), 2000);
    } catch (err) {
      console.error('❌ Error copying QR code:', err);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shortUrl);
      setCopySuccess('✅');
      setTimeout(() => setCopySuccess(''), 2000);
    } catch (err) {
      setCopySuccess('✅');
      setTimeout(() => setCopySuccess(''), 2000);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = () => {
    if (!link.isActive) return '#6c757d';
    if (isExpired) return '#dc3545';
    return '#28a745';
  };

  const getStatusText = () => {
    if (!link.isActive) return 'Inactive';
    if (isExpired) return 'Expired';
    return 'Active';
  };

  return (
    <div className={`modern-link-card ${isSelected ? 'selected' : ''}`}>
      {/* Thumbnail Section */}
      <div className="link-thumbnail">
        {link.imageUrl ? (
          <img src={link.imageUrl} alt="Link preview" className="thumbnail-image" />
        ) : (
          <div className="thumbnail-placeholder">
            <span className="placeholder-icon">🔗</span>
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="link-content">
        <div className="link-header">
          <div className="link-info">
            <h3 className="link-title">{link.title || 'Untitled Link'}</h3>
            <div className="link-urls">
              <div className="short-url-container">
                <code className="short-url">{shortUrl}</code>
                <button
                  className="copy-button"
                  onClick={copyToClipboard}
                  title="Copy to clipboard"
                >
                  {copySuccess || '📋'}
                </button>
              </div>
              <div className="original-url">
                <a
                  href={link.originalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="original-link"
                  title={link.originalUrl}
                >
                  {link.originalUrl.length > 80
                    ? `${link.originalUrl.substring(0, 80)}...`
                    : link.originalUrl
                  }
                </a>
              </div>
            </div>

            {link.description && (
              <div className="link-description">
                {link.description}
              </div>
            )}

            <div className="link-meta">
              <div className="meta-badges">
                <span
                  className="status-badge"
                  style={{ backgroundColor: getStatusColor() }}
                >
                  {getStatusText()}
                </span>

                {(domain || link.domain) && (
                  <span className="domain-badge">
                    🌐 {domainName}
                  </span>
                )}

                {isPasswordProtected && (
                  <span className="password-badge">
                    🔒 Protected
                  </span>
                )}

                {link.tags && link.tags.length > 0 && (
                  link.tags.slice(0, 3).map((tag, index) => (
                    <span key={index} className="tag-badge">
                      {tag}
                    </span>
                  ))
                )}
              </div>

              <div className="meta-info">
                <span className="click-count">{link.clickCount || 0} clicks</span>
                <span className="created-date">Created {formatDate(link.createdAt)}</span>
              </div>
            </div>
          </div>

          <div className="link-actions">
            <button
              className={`action-btn qr-btn ${showQR ? 'active' : ''}`}
              onClick={toggleQR}
              title="Toggle QR code"
            >
              📱
            </button>
            <button
              className="action-btn share-btn"
              onClick={() => onShare && onShare(link)}
              title="Share & QR code"
            >
              📤
            </button>
            <button
              className="action-btn edit-btn"
              onClick={() => onEdit(link)}
              title="Edit link"
            >
              ✏️
            </button>
            <button
              className="action-btn delete-btn"
              onClick={() => onDelete(link.id)}
              title="Delete link"
            >
              🗑️
            </button>
          </div>
        </div>

        {/* QR Code Section */}
        {showQR && (
          <div className="qr-section">
            <div className="qr-display">
              {qrDataUrl ? (
                <div className="qr-container">
                  <img src={qrDataUrl} alt="QR Code" className="qr-image" />
                  <div className="qr-actions">
                    <button className="qr-action-btn" onClick={copyQRCode}>
                      📋 Copy QR
                    </button>
                    <button
                      className="qr-action-btn"
                      onClick={() => {
                        const link = document.createElement('a');
                        link.download = `qr-${link.shortCode}.png`;
                        link.href = qrDataUrl;
                        link.click();
                      }}
                    >
                      📥 Download
                    </button>
                  </div>
                </div>
              ) : (
                <div className="qr-loading">
                  <div className="mini-spinner"></div>
                  <span>Generating QR...</span>
                </div>
              )}
            </div>
          </div>
        )}
    </div>

      <style jsx>{`
        .modern-link-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          padding: 20px;
          transition: all 0.3s ease;
          display: flex;
          gap: 20px;
          align-items: flex-start;
          min-height: 120px;
        }

        .modern-link-card:hover {
          border-color: #d1d5db;
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
          transform: translateY(-1px);
        }

        .modern-link-card.selected {
          border-color: #667eea;
          box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
        }

        .link-thumbnail {
          flex-shrink: 0;
          width: 80px;
          height: 80px;
          border-radius: 12px;
          overflow: hidden;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
        }

        .thumbnail-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .thumbnail-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #f9fafb 0%, #e5e7eb 100%);
          color: #6b7280;
        }

        .placeholder-icon {
          font-size: 24px;
        }

        .link-content {
          flex: 1;
          min-width: 0;
        }

        .link-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 16px;
        }

        .link-info {
          flex: 1;
          min-width: 0;
        }

        .link-title {
          margin: 0 0 8px 0;
          font-size: 18px;
          font-weight: 600;
          color: #111827;
          line-height: 1.3;
        }

        .link-urls {
          margin-bottom: 12px;
        }

        .short-url-container {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 6px;
        }

        .short-url {
          background: #f3f4f6;
          color: #667eea;
          padding: 6px 10px;
          border-radius: 6px;
          font-family: 'Monaco', 'Menlo', monospace;
          font-size: 14px;
          border: 1px solid #e5e7eb;
        }

        .copy-button {
          background: #667eea;
          color: white;
          border: none;
          padding: 6px 8px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 12px;
          transition: all 0.2s ease;
        }

        .copy-button:hover {
          background: #5a6fd8;
          transform: scale(1.05);
        }

        .original-url {
          margin-left: 4px;
        }

        .original-link {
          color: #6b7280;
          text-decoration: none;
          font-size: 14px;
          word-break: break-all;
        }

        .original-link:hover {
          color: #667eea;
        }

        .link-description {
          color: #6b7280;
          font-size: 14px;
          line-height: 1.4;
          margin-bottom: 12px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .link-meta {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          gap: 12px;
        }

        .meta-badges {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
          align-items: center;
        }

        .status-badge,
        .domain-badge,
        .password-badge,
        .tag-badge {
          font-size: 11px;
          padding: 4px 8px;
          border-radius: 8px;
          font-weight: 500;
          white-space: nowrap;
        }

        .status-badge {
          color: white;
        }

        .domain-badge {
          background: #dbeafe;
          color: #1d4ed8;
        }

        .password-badge {
          background: #fef3c7;
          color: #d97706;
        }

        .tag-badge {
          background: #f3f4f6;
          color: #374151;
        }

        .meta-info {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 4px;
          font-size: 12px;
          color: #6b7280;
          text-align: right;
        }

        .click-count {
          font-weight: 600;
          color: #111827;
        }

        .link-actions {
          display: flex;
          gap: 6px;
          flex-shrink: 0;
        }

        .action-btn {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          padding: 8px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 16px;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
        }

        .action-btn:hover {
          background: #f3f4f6;
          transform: translateY(-1px);
        }

        .share-btn:hover {
          background: #dbeafe;
          border-color: #93c5fd;
        }

        .edit-btn:hover {
          background: #fef3c7;
          border-color: #fcd34d;
        }

        .delete-btn:hover {
          background: #fee2e2;
          border-color: #fca5a5;
        }

        .qr-btn.active {
          background: #dbeafe;
          border-color: #93c5fd;
          color: #1d4ed8;
        }

        .qr-section {
          border-top: 1px solid #e5e7eb;
          padding-top: 16px;
          margin-top: 16px;
        }

        .qr-display {
          display: flex;
          justify-content: center;
        }

        .qr-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }

        .qr-image {
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          border: 1px solid #e5e7eb;
        }

        .qr-actions {
          display: flex;
          gap: 8px;
        }

        .qr-action-btn {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .qr-action-btn:hover {
          background: #f3f4f6;
          transform: translateY(-1px);
        }

        .qr-loading {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #6b7280;
          font-size: 12px;
        }

        .mini-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid #f3f4f6;
          border-top: 2px solid #667eea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
          .modern-link-card {
            flex-direction: column;
            gap: 16px;
          }

          .link-thumbnail {
            width: 100%;
            height: 120px;
          }

          .link-header {
            flex-direction: column;
            gap: 12px;
          }

          .meta-info {
            align-items: flex-start;
            text-align: left;
          }

          .link-actions {
            align-self: flex-end;
          }
        }
      `}</style>
    </div>
  );
};

export default ModernLinkCard;